/**
 * Server settings > AutoMod (2026-10-29), as Discord lays it out: three
 * cards, one per kind of rule (custom words, up to 6 rules; mention spam;
 * spam-like content), each rule a row with its switch that opens its editor
 * in place. Manage Server holders see it while the console's `automod` flag
 * is on; a timeout response needs Moderate Members too (the server checks
 * both). Rules follow AUTO_MODERATION_RULE_* live. One column everywhere, so
 * a phone shows it as it is (the layer gives it Back and the page title).
 *
 * Also here: the channel settings' slowmode control (`SlowmodeField`).
 */
import {
  AUTOMOD_LIMITS,
  AUTOMOD_SPAM,
  AUTOMOD_TIMEOUT_CHOICES,
  AutoModActionType,
  AutoModEventType,
  AutoModTriggerType,
  ChannelType,
  keywordProblem,
  Permissions,
  SLOWMODE_CHOICES,
  type AutoModAction,
  type AutoModRule,
  type CreateAutoModRuleRequest,
  type Snowflake,
} from '@neosocial/protocol';
import { useEffect, useMemo, useState } from 'react';
import { useT } from '../i18n';
import { api } from '../lib/api';
import { TIMEOUT_CHOICES } from '../mod/ModDialogs';
import { reportError } from '../hub/social';
import { useConfig } from '../store/config';
import { useData, useDataShallow } from '../store/data';
import {
  basePermissions,
  can,
  channelIcon,
  sortedRoles,
  useActiveGuildId,
  useChannelTree,
} from '../store/selectors';
import { slowmodeLabel } from '../store/slowmode';
import { onGatewayEvent } from '../store/umEvents';
import { Btn, Icon, Select, Switch, type IconName } from '../ui/kit';
import { confirmDialog, toast } from '../ui/overlay';

/* ------------------------------------------------------------ slowmode */

/** The channel settings' slowmode: Discord's lengths (a value set elsewhere is kept as an option). */
export function SlowmodeField({
  value,
  disabled,
  onChange,
}: {
  value: number;
  disabled?: boolean;
  onChange(v: number): void;
}) {
  const { t } = useT();
  const choices = SLOWMODE_CHOICES.includes(value)
    ? SLOWMODE_CHOICES
    : [...SLOWMODE_CHOICES, value];
  return (
    <label className="ns-field" data-slowmode="">
      <span className="ns-label">{t('sm.label')}</span>
      <Select
        className="nx-input ns-select"
        value={String(value)}
        disabled={disabled}
        onChange={(e) => onChange(Number(e.target.value) || 0)}
      >
        {[...choices]
          .sort((a, b) => a - b)
          .map((s) => (
            <option key={s} value={String(s)}>
              {slowmodeLabel(t, s)}
            </option>
          ))}
      </Select>
      <span className="ns-help">{t('sm.help')}</span>
    </label>
  );
}

/* --------------------------------------------------------------- rules */

type Kind =
  | typeof AutoModTriggerType.KEYWORD
  | typeof AutoModTriggerType.MENTION_SPAM
  | typeof AutoModTriggerType.SPAM;

const KINDS: { kind: Kind; icon: IconName; title: string; sub: string; name: string }[] = [
  {
    kind: AutoModTriggerType.KEYWORD,
    icon: 'filter',
    title: 'am.kw.title',
    sub: 'am.kw.sub',
    name: 'am.kw.default',
  },
  {
    kind: AutoModTriggerType.MENTION_SPAM,
    icon: 'at',
    title: 'am.mention.title',
    sub: 'am.mention.sub',
    name: 'am.mention.default',
  },
  {
    kind: AutoModTriggerType.SPAM,
    icon: 'zap',
    title: 'am.spam.title',
    sub: 'am.spam.sub',
    name: 'am.spam.default',
  },
];

/** An editor's draft of one rule. */
export interface RuleDraft {
  name: string;
  words: string;
  allow: string;
  mentions: number;
  block: boolean;
  custom: string;
  alert: boolean;
  alertChannel: Snowflake | '';
  timeout: boolean;
  timeoutS: number;
  exemptRoles: Snowflake[];
  exemptChannels: Snowflake[];
  enabled: boolean;
}

/** Words from the field: one per line or comma, trimmed, without repeats. */
export function parseWords(text: string): string[] {
  return [
    ...new Set(
      text
        .split(/[\n,،]/)
        .map((w) => w.trim())
        .filter(Boolean),
    ),
  ];
}

const actionOf = (r: AutoModRule | undefined, type: AutoModActionType) =>
  r?.actions.find((a) => a.type === type);

export function draftOfRule(r: AutoModRule | undefined, defaultName: string): RuleDraft {
  const block = actionOf(r, AutoModActionType.BLOCK_MESSAGE);
  const alert = actionOf(r, AutoModActionType.SEND_ALERT_MESSAGE);
  const timeout = actionOf(r, AutoModActionType.TIMEOUT);
  return {
    name: r?.name ?? defaultName,
    words: (r?.trigger_metadata.keyword_filter ?? []).join('\n'),
    allow: (r?.trigger_metadata.allow_list ?? []).join('\n'),
    mentions: r?.trigger_metadata.mention_total_limit ?? AUTOMOD_LIMITS.MENTION_LIMIT_DEFAULT,
    // a new rule blocks (Discord's default)
    block: r ? !!block : true,
    custom: block?.metadata?.custom_message ?? '',
    alert: !!alert,
    alertChannel: alert?.metadata?.channel_id ?? '',
    timeout: !!timeout,
    timeoutS: timeout?.metadata?.duration_seconds ?? 60,
    exemptRoles: r?.exempt_roles ?? [],
    exemptChannels: r?.exempt_channels ?? [],
    enabled: r?.enabled ?? true,
  };
}

/** What is wrong with a draft (an i18n key and its values), or null. */
export function draftProblem(
  d: RuleDraft,
  kind: Kind,
  maxWords: number,
): { key: string; n?: number } | null {
  if (kind === AutoModTriggerType.KEYWORD) {
    const words = parseWords(d.words);
    if (words.length === 0) return { key: 'am.err.noWords' };
    if (words.length > maxWords) return { key: 'am.err.tooMany', n: maxWords };
    if ([...words, ...parseWords(d.allow)].some((w) => keywordProblem(w)))
      return { key: 'am.err.badWord' };
  }
  const timeout = d.timeout && kind !== AutoModTriggerType.SPAM;
  if (!d.block && !d.alert && !timeout) return { key: 'am.err.noAction' };
  if (d.alert && !d.alertChannel) return { key: 'am.err.noChannel' };
  return null;
}

/** The request body for a draft (create; a PATCH sends the same without `trigger_type`). */
export function ruleBody(d: RuleDraft, kind: Kind): CreateAutoModRuleRequest {
  const actions: AutoModAction[] = [];
  if (d.block)
    actions.push({
      type: AutoModActionType.BLOCK_MESSAGE,
      metadata: d.custom.trim() ? { custom_message: d.custom.trim() } : {},
    });
  if (d.alert && d.alertChannel)
    actions.push({
      type: AutoModActionType.SEND_ALERT_MESSAGE,
      metadata: { channel_id: d.alertChannel },
    });
  if (d.timeout && kind !== AutoModTriggerType.SPAM)
    actions.push({ type: AutoModActionType.TIMEOUT, metadata: { duration_seconds: d.timeoutS } });
  return {
    name: d.name.trim(),
    event_type: AutoModEventType.MESSAGE_SEND,
    trigger_type: kind,
    trigger_metadata:
      kind === AutoModTriggerType.KEYWORD
        ? { keyword_filter: parseWords(d.words), allow_list: parseWords(d.allow) }
        : kind === AutoModTriggerType.MENTION_SPAM
          ? { mention_total_limit: d.mentions }
          : {},
    actions,
    enabled: d.enabled,
    exempt_roles: d.exemptRoles,
    exempt_channels: d.exemptChannels,
  };
}

type Load = { st: 'loading' } | { st: 'error' } | { st: 'ok'; rules: AutoModRule[] };

export function AutoModTab() {
  const { t } = useT();
  const gid = useActiveGuildId();
  const [load, setLoad] = useState<Load>({ st: 'loading' });
  const [tick, setTick] = useState(0);
  // which editor is open: a rule's id, or 'new:<kind>'
  const [open, setOpen] = useState<string | null>(null);
  useEffect(() => {
    if (!gid) return;
    let live = true;
    api.automod.rules(gid).then(
      (rules) => live && setLoad({ st: 'ok', rules }),
      () => live && setLoad({ st: 'error' }),
    );
    return () => {
      live = false;
    };
  }, [gid, tick]);
  // other managers' changes, live
  useEffect(
    () =>
      onGatewayEvent((f) => {
        if (
          f.t !== 'AUTO_MODERATION_RULE_CREATE' &&
          f.t !== 'AUTO_MODERATION_RULE_UPDATE' &&
          f.t !== 'AUTO_MODERATION_RULE_DELETE'
        )
          return;
        const r = f.d;
        if (r.guild_id !== gid) return;
        setLoad((l) => {
          if (l.st !== 'ok') return l;
          const rest = l.rules.filter((x) => x.id !== r.id);
          return {
            st: 'ok',
            rules: f.t === 'AUTO_MODERATION_RULE_DELETE' ? rest : [...rest, r].sort(byId),
          };
        });
      }),
    [gid],
  );
  const upsert = (r: AutoModRule) =>
    setLoad((l) =>
      l.st === 'ok'
        ? { st: 'ok', rules: [...l.rules.filter((x) => x.id !== r.id), r].sort(byId) }
        : l,
    );
  const drop = (id: Snowflake) =>
    setLoad((l) => (l.st === 'ok' ? { st: 'ok', rules: l.rules.filter((x) => x.id !== id) } : l));

  return (
    <div className="ns-tool ns-am">
      <p className="ns-muted">{t('am.intro')}</p>
      {load.st === 'loading' ? (
        <p className="ns-muted">
          <i className="nx-spin" /> {t('ns.loading')}
        </p>
      ) : load.st === 'error' ? (
        <p className="ns-note warn" role="alert">
          <Icon name="alert" /> {t('am.loadFail')}{' '}
          <button type="button" className="ns-link" onClick={() => setTick((n) => n + 1)}>
            {t('am.retry')}
          </button>
        </p>
      ) : (
        KINDS.map((k) => {
          const rules = load.rules.filter((r) => r.trigger_type === k.kind);
          const max = AUTOMOD_LIMITS.RULES_PER_TYPE[k.kind] ?? 1;
          const newKey = `new:${k.kind}`;
          return (
            <section key={k.kind} className="ns-am-kind" data-kind={k.kind}>
              <header className="ns-am-kind-h">
                <span className="ns-am-ic" aria-hidden="true">
                  <Icon name={k.icon} size="m" />
                </span>
                <span className="ns-am-kind-t">
                  <b>{t(k.title)}</b>
                  <span className="ns-muted">{t(k.sub, { n: AUTOMOD_SPAM.REPEATS })}</span>
                </span>
                {rules.length < max && open !== newKey ? (
                  <Btn size="sm" kind="secondary" icon="plus" onClick={() => setOpen(newKey)}>
                    {t('am.new')}
                  </Btn>
                ) : null}
              </header>
              {rules.map((r) => (
                <RuleRow
                  key={r.id}
                  rule={r}
                  kind={k.kind}
                  open={open === r.id}
                  onOpen={(on) => setOpen(on ? r.id : null)}
                  onSaved={upsert}
                  onDeleted={drop}
                />
              ))}
              {open === newKey && gid ? (
                <RuleEditor
                  kind={k.kind}
                  defaultName={t(k.name)}
                  onClose={() => setOpen(null)}
                  onSaved={(r) => {
                    upsert(r);
                    setOpen(null);
                  }}
                />
              ) : null}
            </section>
          );
        })
      )}
    </div>
  );
}

const byId = (a: AutoModRule, b: AutoModRule) =>
  a.id.length - b.id.length || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);

/** A rule's one-line summary: what it catches and what it does. */
function useSummary(rule: AutoModRule): string {
  const { t } = useT();
  const channels = useData((d) => d.channels);
  const parts: string[] = [];
  if (rule.trigger_type === AutoModTriggerType.KEYWORD)
    parts.push(t('am.summary.words', { n: rule.trigger_metadata.keyword_filter?.length ?? 0 }));
  if (rule.trigger_type === AutoModTriggerType.MENTION_SPAM)
    parts.push(t('am.summary.mentions', { n: rule.trigger_metadata.mention_total_limit ?? 0 }));
  for (const a of rule.actions) {
    if (a.type === AutoModActionType.BLOCK_MESSAGE) parts.push(t('am.summary.blocks'));
    if (a.type === AutoModActionType.SEND_ALERT_MESSAGE)
      parts.push(
        t('am.summary.alerts', { channel: channels[a.metadata?.channel_id ?? '']?.name ?? '—' }),
      );
    if (a.type === AutoModActionType.TIMEOUT)
      parts.push(
        t('am.summary.timeout', { time: timeoutLabel(t, a.metadata?.duration_seconds ?? 0) }),
      );
  }
  return parts.join(' · ');
}

function timeoutLabel(t: (k: string, v?: Record<string, number>) => string, s: number): string {
  const c = TIMEOUT_CHOICES.find((x) => x.s === s);
  return c ? t(c.key) : slowmodeLabel(t, s);
}

function RuleRow({
  rule,
  kind,
  open,
  onOpen,
  onSaved,
  onDeleted,
}: {
  rule: AutoModRule;
  kind: Kind;
  open: boolean;
  onOpen(on: boolean): void;
  onSaved(r: AutoModRule): void;
  onDeleted(id: Snowflake): void;
}) {
  const { t } = useT();
  const gid = useActiveGuildId();
  const summary = useSummary(rule);
  const [busy, setBusy] = useState(false);
  const toggle = async (on: boolean) => {
    if (!gid) return;
    setBusy(true);
    try {
      onSaved(await api.automod.modify(gid, rule.id, { enabled: on }));
    } catch (e) {
      reportError(e);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="ns-am-rule" data-open={open || undefined} data-rule={rule.id}>
      <div className="ns-am-rule-h">
        <span className="ns-am-rule-t">
          <b dir="auto">{rule.name}</b>
          <span className="ns-muted">{rule.enabled ? summary : t('am.summary.off')}</span>
        </span>
        <Switch
          checked={rule.enabled}
          disabled={busy}
          label={t('am.enabled')}
          onChange={(on) => void toggle(on)}
        />
        <Btn size="sm" kind="ghost" icon={open ? 'chev-up' : 'edit'} onClick={() => onOpen(!open)}>
          {open ? t('am.cancel') : t('am.edit')}
        </Btn>
      </div>
      {open ? (
        <RuleEditor
          rule={rule}
          kind={kind}
          defaultName={rule.name}
          onClose={() => onOpen(false)}
          onSaved={(r) => {
            onSaved(r);
            onOpen(false);
          }}
          onDeleted={() => {
            onDeleted(rule.id);
            onOpen(false);
          }}
        />
      ) : null}
    </div>
  );
}

function RuleEditor({
  rule,
  kind,
  defaultName,
  onClose,
  onSaved,
  onDeleted,
}: {
  rule?: AutoModRule;
  kind: Kind;
  defaultName: string;
  onClose(): void;
  onSaved(r: AutoModRule): void;
  onDeleted?(): void;
}) {
  const { t, fmt } = useT();
  const gid = useActiveGuildId();
  const [d, setD] = useState<RuleDraft>(() => draftOfRule(rule, defaultName));
  const [busy, setBusy] = useState(false);
  const [tried, setTried] = useState(false);
  const patch = (p: Partial<RuleDraft>) => setD((x) => ({ ...x, ...p }));
  // the console's limits.automod_keywords (the server enforces it too)
  const wordLimit = useConfig((s) => s.config?.settings['limits.automod_keywords']);
  const maxWords = Math.min(AUTOMOD_LIMITS.KEYWORDS_MAX, wordLimit ?? AUTOMOD_LIMITS.KEYWORDS_MAX);
  // a timeout response needs Moderate Members (the server's rule, Discord's)
  const canTimeout = useData((s) => {
    const g = gid ? s.guilds[gid] : undefined;
    return g?.owner_id === s.me?.id || can(basePermissions(s, gid), Permissions.MODERATE_MEMBERS);
  });
  const tree = useChannelTree(gid);
  const textChannels = useMemo(
    () =>
      tree
        .flatMap((n) => n.channels)
        .filter(
          (c) => c.type === ChannelType.GUILD_TEXT || c.type === ChannelType.GUILD_ANNOUNCEMENT,
        ),
    [tree],
  );
  const exemptable = useMemo(
    () => tree.flatMap((n) => [...(n.category ? [n.category] : []), ...n.channels]),
    [tree],
  );
  const roles = useDataShallow((s) =>
    sortedRoles(gid ? s.guilds[gid] : undefined).filter((r) => r.id !== gid),
  );
  const problem = draftProblem(d, kind, maxWords);
  const wordCount = parseWords(d.words).length;
  const save = async () => {
    setTried(true);
    if (!gid || problem || !d.name.trim()) return;
    setBusy(true);
    try {
      const body = ruleBody(d, kind);
      const { trigger_type: _kind, ...edit } = body;
      const saved = rule
        ? await api.automod.modify(gid, rule.id, edit)
        : await api.automod.create(gid, body);
      toast(t('am.saved'), { icon: 'check', tone: 'ok' });
      onSaved(saved);
    } catch (e) {
      reportError(e);
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    if (!gid || !rule) return;
    const ok = await confirmDialog({
      title: t('am.deleteTitle', { name: rule.name }),
      body: t('am.deleteBody'),
      confirmLabel: t('am.delete'),
      danger: true,
      icon: 'trash',
    });
    if (!ok) return;
    try {
      await api.automod.remove(gid, rule.id);
      toast(t('am.deleted'), { icon: 'trash' });
      onDeleted?.();
    } catch (e) {
      reportError(e);
    }
  };
  const roleName = (id: Snowflake) => roles.find((r) => r.id === id)?.name ?? '—';
  const channelName = (id: Snowflake) => exemptable.find((c) => c.id === id)?.name ?? '—';

  return (
    <div className="ns-am-ed" data-kind={kind}>
      <label className="ns-field">
        <span className="ns-label">{t('am.f.name')}</span>
        <input
          className="nx-input"
          dir="auto"
          value={d.name}
          maxLength={AUTOMOD_LIMITS.NAME_MAX}
          onChange={(e) => patch({ name: e.target.value })}
        />
      </label>

      {kind === AutoModTriggerType.KEYWORD ? (
        <>
          <label className="ns-field">
            <span className="ns-label ns-am-label">
              {t('am.f.words')}
              <span className="ns-muted" data-count="">
                {t('am.f.wordsCount', { n: fmt.num(wordCount), max: fmt.num(maxWords) })}
              </span>
            </span>
            <textarea
              className="nx-textarea"
              dir="auto"
              rows={4}
              value={d.words}
              aria-invalid={(tried && !!problem && problem.key !== 'am.err.noAction') || undefined}
              onChange={(e) => patch({ words: e.target.value })}
            />
            <span className="ns-help">{t('am.f.wordsHelp')}</span>
          </label>
          <label className="ns-field">
            <span className="ns-label">{t('am.f.allow')}</span>
            <textarea
              className="nx-textarea"
              dir="auto"
              rows={2}
              value={d.allow}
              onChange={(e) => patch({ allow: e.target.value })}
            />
            <span className="ns-help">{t('am.f.allowHelp')}</span>
          </label>
        </>
      ) : kind === AutoModTriggerType.MENTION_SPAM ? (
        <label className="ns-field">
          <span className="ns-label">{t('am.f.mentions')}</span>
          <input
            className="nx-input ns-num-in"
            type="number"
            min={AUTOMOD_LIMITS.MENTION_LIMIT_MIN}
            max={AUTOMOD_LIMITS.MENTION_LIMIT_MAX}
            value={d.mentions}
            onChange={(e) =>
              patch({
                mentions: Math.max(
                  AUTOMOD_LIMITS.MENTION_LIMIT_MIN,
                  Math.min(AUTOMOD_LIMITS.MENTION_LIMIT_MAX, Number(e.target.value) || 1),
                ),
              })
            }
          />
          <span className="ns-help">{t('am.f.mentionsHelp')}</span>
        </label>
      ) : (
        <p className="ns-help">{t('am.f.spamHelp', { n: AUTOMOD_SPAM.REPEATS })}</p>
      )}

      <h4 className="ns-am-sub">{t('am.f.actions')}</h4>
      <div className="ns-am-act">
        <div className="ns-switch-row">
          <span className="ns-switch-l">
            {t('am.f.block')}
            <span className="ns-help">{t('am.f.blockHelp')}</span>
          </span>
          <Switch
            checked={d.block}
            label={t('am.f.block')}
            onChange={(block) => patch({ block })}
          />
        </div>
        {d.block ? (
          <label className="ns-field">
            <span className="ns-label">{t('am.f.custom')}</span>
            <input
              className="nx-input"
              dir="auto"
              value={d.custom}
              maxLength={AUTOMOD_LIMITS.CUSTOM_MESSAGE_MAX}
              placeholder={t('am.f.customPh')}
              onChange={(e) => patch({ custom: e.target.value })}
            />
          </label>
        ) : null}
      </div>
      <div className="ns-am-act">
        <div className="ns-switch-row">
          <span className="ns-switch-l">
            {t('am.f.alert')}
            <span className="ns-help">{t('am.f.alertHelp')}</span>
          </span>
          <Switch
            checked={d.alert}
            label={t('am.f.alert')}
            onChange={(alert) => patch({ alert })}
          />
        </div>
        {d.alert ? (
          <label className="ns-field">
            <span className="ns-label">{t('am.f.alertChannel')}</span>
            <Select
              className="nx-input ns-select"
              value={d.alertChannel}
              aria-invalid={(tried && !d.alertChannel) || undefined}
              onChange={(e) => patch({ alertChannel: e.target.value })}
            >
              <option value="">{t('am.f.pickChannel')}</option>
              {textChannels.map((c) => (
                <option key={c.id} value={c.id}>
                  {'#' + (c.name ?? '')}
                </option>
              ))}
            </Select>
          </label>
        ) : null}
      </div>
      {kind !== AutoModTriggerType.SPAM ? (
        <div className="ns-am-act">
          <div className="ns-switch-row">
            <span className="ns-switch-l">
              {t('am.f.timeout')}
              <span className="ns-help">
                {canTimeout ? t('am.f.timeoutHelp') : t('am.f.timeoutNeeds')}
              </span>
            </span>
            <Switch
              checked={d.timeout}
              disabled={!canTimeout}
              label={t('am.f.timeout')}
              onChange={(timeout) => patch({ timeout })}
            />
          </div>
          {d.timeout ? (
            <label className="ns-field">
              <span className="ns-label">{t('am.f.timeoutFor')}</span>
              <Select
                className="nx-input ns-select"
                value={String(d.timeoutS)}
                disabled={!canTimeout}
                onChange={(e) => patch({ timeoutS: Number(e.target.value) })}
              >
                {AUTOMOD_TIMEOUT_CHOICES.map((s) => (
                  <option key={s} value={String(s)}>
                    {timeoutLabel(t, s)}
                  </option>
                ))}
              </Select>
            </label>
          ) : null}
        </div>
      ) : null}

      <h4 className="ns-am-sub">{t('am.f.exempt')}</h4>
      <IdPicker
        label={t('am.f.exemptRoles')}
        ids={d.exemptRoles}
        max={AUTOMOD_LIMITS.EXEMPT_ROLES_MAX}
        options={roles.map((r) => ({ id: r.id, name: r.name, icon: 'shield' as IconName }))}
        nameOf={roleName}
        onChange={(exemptRoles) => patch({ exemptRoles })}
      />
      <IdPicker
        label={t('am.f.exemptChannels')}
        ids={d.exemptChannels}
        max={AUTOMOD_LIMITS.EXEMPT_CHANNELS_MAX}
        options={exemptable.map((c) => ({
          id: c.id,
          name: c.name ?? '',
          icon: channelIcon(c.type) as IconName,
        }))}
        nameOf={channelName}
        onChange={(exemptChannels) => patch({ exemptChannels })}
      />

      {tried && problem ? (
        <p className="ns-note warn" role="alert" data-problem={problem.key}>
          <Icon name="alert" /> {t(problem.key, problem.n !== undefined ? { n: problem.n } : {})}
        </p>
      ) : null}
      <div className="ns-row-btns end ns-am-foot">
        {rule ? (
          <Btn kind="danger-quiet" size="sm" icon="trash" onClick={() => void remove()}>
            {t('am.delete')}
          </Btn>
        ) : null}
        <span className="ns-fill" />
        <Btn kind="ghost" size="sm" onClick={onClose} disabled={busy}>
          {t('am.cancel')}
        </Btn>
        <Btn
          kind="primary"
          size="sm"
          busy={busy}
          disabled={busy || !d.name.trim()}
          onClick={() => void save()}
        >
          {t('am.save')}
        </Btn>
      </div>
    </div>
  );
}

/** Chips of picked roles or channels, each removable, and a picker to add one more. */
function IdPicker({
  label,
  ids,
  max,
  options,
  nameOf,
  onChange,
}: {
  label: string;
  ids: Snowflake[];
  max: number;
  options: { id: Snowflake; name: string; icon: IconName }[];
  nameOf(id: Snowflake): string;
  onChange(ids: Snowflake[]): void;
}) {
  const { t } = useT();
  const left = options.filter((o) => !ids.includes(o.id));
  return (
    <div className="ns-field ns-am-ids">
      <span className="ns-label">{label}</span>
      {ids.length ? (
        <ul className="ns-am-chips">
          {ids.map((id) => (
            <li key={id}>
              <span className="ns-am-chip">
                <bdi>{nameOf(id)}</bdi>
                <button
                  type="button"
                  aria-label={t('am.f.remove', { name: nameOf(id) })}
                  onClick={() => onChange(ids.filter((x) => x !== id))}
                >
                  <Icon name="x" size="s" />
                </button>
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <span className="ns-muted">{t('am.f.none')}</span>
      )}
      {ids.length < max && left.length ? (
        <Select
          className="nx-input ns-select"
          value=""
          aria-label={`${t('am.f.add')}: ${label}`}
          onChange={(e) => e.target.value && onChange([...ids, e.target.value])}
        >
          <option value="">{t('am.f.add')}</option>
          {left.map((o) => (
            <option key={o.id} value={o.id}>
              {o.name}
            </option>
          ))}
        </Select>
      ) : null}
    </div>
  );
}
